// SpotifyIntegration.js
import React, { useState } from "react";
import axios from "axios";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Alert, Form } from "react-bootstrap";
import "./SpotifyIntegration.css";

const SpotifyIntegration = () => {
    const [token, setToken] = useState(null);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("track");
    const [results, setResults] = useState([]);
    const [albumTracks, setAlbumTracks] = useState([]);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState(null);

    const getSpotifyToken = async () => {
        const clientId = "c05e7e99fde84c31bf1ac4856abe263b";
        const clientSecret = "4179cfa3d13c4449b752159a4ae76339";
        const authString = btoa(`${clientId}:${clientSecret}`);

        try {
            const response = await axios.post(
                "https://accounts.spotify.com/api/token",
                "grant_type=client_credentials",
                {
                    headers: {
                        Authorization: `Basic ${authString}`,
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                }
            );
            setToken(response.data.access_token);
            setMessage("Token de Spotify obtenido correctamente.");
        } catch (err) {
            setError("Error al obtener el token de Spotify.");
            console.error(err);
        }
    };

    const searchSpotify = async () => {
        if (!token) {
            setError("Token no disponible. Obtén el token primero.");
            return;
        }

        try {
            const response = await axios.get(
                `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=${filter}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            let items = response.data[`${filter}s`]?.items || [];

            // Filtrar álbumes válidos con ID y al menos una imagen
            if (filter === "album") {
                items = items.filter(album => album.id && album.images && album.images.length > 0);
            }

            setResults(items);
            setAlbumTracks([]);
            setSelectedAlbum(null);
            setMessage(`Resultados para: "${query}" (${filter})`);
        } catch (err) {
            setError("Error al buscar en Spotify.");
            console.error(err);
        }
    };

    const fetchAlbumTracks = async (album) => {
        if (!token) {
            setError("Token no disponible. Obtén el token primero.");
            return;
        }

        if (!album?.id) {
            setError("ID del álbum no válido.");
            return;
        }

        try {
            const response = await axios.get(
                `https://api.spotify.com/v1/albums/${album.id}/tracks`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (!response.data || !response.data.items) {
                throw new Error("El álbum no tiene canciones disponibles.");
            }

            setAlbumTracks(response.data.items);
            setSelectedAlbum(album);
            setMessage(`Canciones del álbum "${album.name}" obtenidas correctamente.`);
        } catch (err) {
            setError(`Error al obtener las canciones del álbum "${album.name}". Verificá si el álbum está disponible.`);
            console.error(err);
        }
    };

    const saveSongToFirebase = async (song) => {
    console.log("Guardando canción:", song); // 👀 Verificá si preview_url está ahí

    try {
        await addDoc(collection(db, "songs"), {
            title: song.name,
            artist: song.artists.map((artist) => artist.name).join(", "),
            url: song.external_urls.spotify,
            imageUrl: song.album?.images[0]?.url || null,
            preview_url: song.preview_url || "", // evitar null
        });
        setMessage(`Canción "${song.name}" guardada correctamente.`);
    } catch (err) {
        setError("Error al guardar la canción en Firestore.");
        console.error(err);
    }
};


    return (
        <div className="container mt-4">
            <h2>Integración con Spotify</h2>
            <div className="mt-3 d-flex justify-content-between">
                <Button onClick={getSpotifyToken} variant="primary">
                    <i className="bi bi-shield-lock"></i> Obtener Token
                </Button>
                <Button onClick={searchSpotify} variant="success">
                    <i className="bi bi-search"></i> Buscar
                </Button>
            </div>
            <Form className="mt-4" onSubmit={(e) => e.preventDefault()}>
                <Form.Group controlId="searchQuery">
                    <Form.Label>Búsqueda en Spotify:</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Ingresa el nombre de la canción, artista o álbum"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </Form.Group>
                <Form.Group controlId="searchFilter" className="mt-2">
                    <Form.Label>Filtrar por:</Form.Label>
                    <Form.Select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    >
                        <option value="track">Canción</option>
                        {/* <option value="artist">Artista</option> */}
                        <option value="album">Álbum</option>
                    </Form.Select>
                </Form.Group>
            </Form>

            {results.length > 0 && filter !== "album" && (
                <div className="mt-4">
                    <h5>Resultados:</h5>
                    <ul className="list-group">
                        {results.map((item) => (
                            <li key={item.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{item.name}</strong> - {item.artists.map((a) => a.name).join(", ")}
                                    {item.preview_url && (
                                        <audio controls src={item.preview_url} className="ms-3" style={{ height: "2rem" }} />
                                    )}
                                </div>
                                <Button onClick={() => saveSongToFirebase(item)} variant="primary">
                                    <i className="bi bi-save"></i> Guardar
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {results.length > 0 && filter === "album" && !selectedAlbum && (
                <div className="mt-4">
                    <h5>Álbumes:</h5>
                    <ul className="list-group">
                        {results.map((album) => (
                            <li key={album.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{album.name}</strong> - {album.artists.map((a) => a.name).join(", ")}
                                    <br /><small className="text-muted">ID: {album.id}</small>
                                </div>
                                <Button onClick={() => fetchAlbumTracks(album)} variant="info">
                                    <i className="bi bi-music-note-list"></i> Ver Canciones
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {albumTracks.length > 0 && selectedAlbum && (
                <div className="mt-4">
                    <h5>Canciones del Álbum: {selectedAlbum.name}</h5>
                    <Button
                        onClick={() => {
                            setAlbumTracks([]);
                            setSelectedAlbum(null);
                        }}
                        variant="secondary"
                        className="mb-2"
                    >
                        <i className="bi bi-arrow-left"></i> Volver a Álbumes
                    </Button>
                    <ul className="list-group">
                        {albumTracks.map((track) => (
                            <li key={track.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{track.name}</strong> - {track.artists.map((a) => a.name).join(", ")}
                                    {track.preview_url && (
                                        <audio controls src={track.preview_url} className="ms-3" style={{ height: "2rem" }} />
                                    )}
                                </div>
                                <Button onClick={() => saveSongToFirebase(track)} variant="primary">
                                    <i className="bi bi-save"></i> Guardar
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {message && <Alert variant="info" className="mt-3">{message}</Alert>}
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        </div>
    );
};

export default SpotifyIntegration;
